import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { LanguageToggle } from './LanguageToggle';
import { NavDropdown } from './NavDropdown';
import { useLanguage } from '../../contexts/LanguageContext';
import { business } from '../../data/site';
import { whatsappLink } from '../../utils/whatsapp';
export function Header() {
  const {
    t
  } = useLanguage();
  const nav = t.common.nav;
  const {
    pathname
  } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  const serviceLinks = t.services.items.map(s => ({
    label: s.title,
    to: `/services/${s.slug}`
  }));
  const f = t.portfolio.filters;
  const portfolioLinks = (['weddings', 'balloons', 'celebrations', 'centerpieces', 'themed'] as const).map(key => ({
    label: f[key],
    to: `/portfolio?category=${key}`
  }));
  const simpleLinks = [{
    label: nav.reviews,
    to: '/reviews'
  }, {
    label: nav.faq,
    to: '/faq'
  }, {
    label: nav.process,
    to: '/process'
  }, {
    label: nav.contact,
    to: '/contact'
  }];
  const linkCls = ({
    isActive
  }: {
    isActive: boolean;
  }) => `flex h-10 items-center whitespace-nowrap text-sm transition-colors duration-150 ease-smooth hover:text-mauve ${isActive ? 'font-medium text-mauve' : 'text-ink/75'}`;
  return <header className={`sticky top-0 z-40 w-full border-b bg-white/95 backdrop-blur transition-colors duration-200 ease-smooth ${scrolled || open ? 'border-petal' : 'border-transparent'}`} data-id="element-176" data-mp="3u2hbtjmh2x3x">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8" data-id="element-177" data-mp="1dbqw2aktkdur">
        <Link to="/" className="shrink-0 leading-none" aria-label={business.name} data-id="element-178" data-mp="3acd916gvdntl">
          <span className="block font-display text-lg font-semibold text-mauve-deep sm:text-xl" data-id="element-179" data-mp="3ae7wosjizu1d">
            Flores con Aroma de Café
          </span>
          <span className="mt-1 block text-[10px] font-medium uppercase tracking-wider2 text-ink/50" data-id="element-180" data-mp="3jc0blvlc4zo8">
            {t.common.kinds.both} · Jessup, MD
          </span>
        </Link>

        <nav aria-label={nav.main} className="hidden items-center gap-6 xl:flex" data-id="element-181" data-mp="2qnw6bb6oeom2">
          <NavLink to="/" end className={linkCls} data-id="element-182" data-mp="2mmsmyepf6ag3">
            {nav.home}
          </NavLink>
          <NavDropdown label={nav.services} items={serviceLinks} footer={{
          label: nav.allServices,
          to: '/services'
        }} active={pathname.startsWith('/services')} data-id="element-183" data-mp="30e7amyu9uzth" />
          <NavDropdown label={nav.portfolio} items={portfolioLinks} footer={{
          label: nav.allWork,
          to: '/portfolio'
        }} active={pathname.startsWith('/portfolio')} data-id="element-184" data-mp="27xed75hxlr06" />
          {simpleLinks.map(l => <NavLink key={l.to} to={l.to} className={linkCls} data-id="element-185" data-mp="ieoau4ph3z8l">
              {l.label}
            </NavLink>)}
        </nav>

        <div className="hidden items-center gap-3 xl:flex" data-id="element-186" data-mp="9gmdq08v1qql">
          <LanguageToggle data-id="element-187" data-mp="9dzv95kflqtl" />
          <a href={whatsappLink()} target="_blank" rel="noreferrer" className="flex h-11 items-center whitespace-nowrap rounded-full bg-mauve px-5 text-sm font-medium text-white transition-colors duration-150 ease-smooth hover:bg-mauve-deep" data-id="element-188" data-mp="eexme7nf5v78">
            {nav.book}
          </a>
        </div>

        <div className="flex items-center gap-2 xl:hidden" data-id="element-189" data-mp="s62coj4wdb4w">
          <LanguageToggle data-id="element-190" data-mp="x55ii5spbldx" />
          <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? nav.closeMenu : nav.openMenu} className="flex h-12 w-12 items-center justify-center rounded-full border border-petal text-mauve-deep transition-colors duration-150 ease-smooth hover:bg-petal/60" data-id="element-191" data-mp="x4dmwuz3tnnn">
            {open ? <XIcon className="h-5 w-5" data-id="element-192" data-mp="3scq2ntwd7i1y" /> : <MenuIcon className="h-5 w-5" data-id="element-193" data-mp="3oczg62dvfo38" />}
          </button>
        </div>
      </div>

      {open ? <div id="mobile-nav" className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-petal bg-white px-5 pb-8 pt-2 xl:hidden" data-id="element-194" data-mp="3jeec1rvi0k9i">
          <nav aria-label={nav.main} className="flex flex-col" data-id="element-195" data-mp="jyhgp4oclpir">
            <MobileLink to="/" label={nav.home} data-id="element-196" data-mp="fwwau3a8ebi2" />
            <MobileGroup title={nav.services} links={[{
          label: nav.allServices,
          to: '/services'
        }, ...serviceLinks]} data-id="element-197" data-mp="fy795nkwqr1s" />
            <MobileGroup title={nav.portfolio} links={[{
          label: nav.allWork,
          to: '/portfolio'
        }, ...portfolioLinks]} data-id="element-198" data-mp="b05urfyfffwn" />
            {simpleLinks.map(l => <MobileLink key={l.to} to={l.to} label={l.label} data-id="element-199" data-mp="2vpd1nz33uz1l" />)}
            <MobileLink to="/about" label={nav.about} data-id="element-200" data-mp="2qp7fgyflu4qw" />
          </nav>
          <div className="mt-6 flex flex-col gap-3" data-id="element-201" data-mp="2gljyykyr8av7">
            <a href={whatsappLink()} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center rounded-full bg-mauve text-sm font-medium text-white" data-id="element-202" data-mp="2gn3kapz9pc3u">
              {nav.bookLong}
            </a>
            <a href={business.phoneHref} className="flex h-12 items-center justify-center gap-2 rounded-full border border-mauve/30 text-sm font-medium text-mauve-deep" data-id="element-203" data-mp="2vb84xzhvrxp9">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-204" data-mp="349jrnqfmmbcf" />
              {nav.call} {business.phoneDisplay}
            </a>
          </div>
        </div> : null}
    </header>;
}
function MobileLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="flex h-12 items-center border-b border-petal/70 text-[15px] text-ink/85" data-id="element-205" data-mp="1ewygz6hjwayy">
      {label}
    </Link>;
}
function MobileGroup({
  title,
  links
}: {
  title: string;
  links: {
    label: string;
    to: string;
  }[];
}) {
  return <details className="group border-b border-petal/70" data-id="element-206" data-mp="1bwo9dm9owmjz">
      <summary className="flex h-12 cursor-pointer list-none items-center justify-between text-[15px] text-ink/85 [&::-webkit-details-marker]:hidden" data-id="element-207" data-mp="16wk30p2hx87h">
        {title}
        <span className="text-mauve transition-transform duration-150 ease-smooth group-open:rotate-45" aria-hidden="true" data-id="element-208" data-mp="1gty6lo3bm2id">
          +
        </span>
      </summary>
      <div className="flex flex-col pb-2 pl-4" data-id="element-209" data-mp="1urmocn23ctj7">
        {links.map(l => <Link key={l.to} to={l.to} className="flex min-h-[44px] items-center text-sm text-ink/70" data-id="element-210" data-mp="1ptl5a24o0u7q">
            {l.label}
          </Link>)}
      </div>
    </details>;
}